import { ref, computed } from "vue";
import dayjs from "dayjs";

// Aktif tab durumu
export const activeTab = ref("historical");

// Date range - son 15 günü varsayılan olarak seçecek şekilde ayarlandı
export const dateRange = ref({
  start: dayjs().subtract(14, "day").toDate(), // 14 gün öncesinden (bugün dahil 15 gün)
  end: dayjs().toDate(), // Bugün
});

// Google entegrasyon durumu
export const isConnected = ref(false);
export const isAuthorized = ref(false);
export const hasPropertyId = ref(false);

// Historical data
export const processedData = ref({
  byCountry: {},
  byDate: {},
  byDevice: {},
  bySource: {},
  rawData: [],
  totals: {
    sessions: 0,
    activeUsers: 0,
    bounceRate: 0,
    conversions: 0,
    totalRevenue: 0,
  },
});

// Realtime data
export const realtimeData = ref({
  totals: {
    activeUsers: 0,
    screenPageViews: 0,
    eventCount: 0,
  },
  byCountry: {},
  byDevice: {},
  timeline: {},
});

// Yükleme durumları
export const loading = ref(false);
export const realtimeLoading = ref(false);

// Realtime verilerini periyodik olarak yenilemek için interval
export const realtimeInterval = ref(null);

// Hesaplanmış özellikler
export const authMessage = computed(() => {
  if (!isConnected.value) {
    return {
      title: "Google Analytics Bağlantısı Gerekli",
      description:
        "Sitenizin performans analizlerini görmek için Google Analytics hesabınızı bağlayın. Ayarlar sayfasına giderek hesap bağlantısını tamamlayabilirsiniz.",
      icon: "analytics"
    };
  } else if (!isAuthorized.value) {
    return {
      title: "Yetkilendirme Hatası",
      description:
        "Google hesabınızın erişim izni güncel değil. Bağlantıyı sıfırlayıp yeniden bağlanmanız gerekiyor.",
      icon: "error_outline"
    };
  } else if (!hasPropertyId.value) {
    return {
      title: "Analytics Mülkü Seçilmeli",
      description:
        "Hangi Analytics mülküne ait verileri görmek istediğinizi belirtmeniz gerekiyor. Ayarlar sayfasından bir Analytics mülkü seçin.",
      icon: "home_work"
    };
  }
  return {
    title: "",
    description: "",
    icon: ""
  };
});

// Historical Data Computed Properties
// Ziyaretçi grafik verileri - tarihe göre
export const visitorChartData = computed(() => {
  // Veri yapısının farklı seviyelerini kontrol edelim
  if (!processedData.value) return [];

  // byDate direkt obje olabilir veya data.byDate şeklinde olabilir
  const byDateData =
    processedData.value.byDate ||
    (processedData.value.data && processedData.value.data.byDate) ||
    {};

  if (Object.keys(byDateData).length === 0) return [];

  // Tarihleri sıralayalım
  const sortedDates = Object.keys(byDateData).sort();

  return sortedDates.map((date) => {
    const dateData = byDateData[date];

    // dateData bir dizi veya nesne olabilir, her iki durumu da ele alalım
    let activeUsers = 0;
    let sessions = 0;

    if (Array.isArray(dateData)) {
      activeUsers = dateData.length;
      sessions = dateData.length;
    } else if (dateData && typeof dateData === "object") {
      activeUsers = dateData.activeUsers || 0;
      sessions = dateData.sessions || 0;
    }

    return {
      date: date, // Formatlanmış tarih servis katmanında yapılacak
      activeUsers,
      sessions,
    };
  });
});

// Ülke grafik verileri
export const countryChartData = computed(() => {
  // API response yapısı değişmiş olabilir, veri yapısını kontrol edelim
  const byCountryData =
    processedData.value.byCountry ||
    (processedData.value.data && processedData.value.data.byCountry) ||
    {};

  if (Object.keys(byCountryData).length === 0) return [];

  return Object.entries(byCountryData)
    .map(([country, data]) => {
      // data bir sayı, dizi veya nesne olabilir
      let activeUsers = 0;

      if (typeof data === "number") {
        activeUsers = data;
      } else if (Array.isArray(data)) {
        activeUsers = data.length;
      } else if (data && typeof data === "object") {
        activeUsers = data.activeUsers || data.users || 0;
      }

      return {
        country,
        activeUsers,
      };
    })
    .sort((a, b) => b.activeUsers - a.activeUsers)
    .slice(0, 10);
});

// Cihaz grafik verileri
export const deviceChartData = computed(() => {
  const byDeviceData =
    processedData.value.byDevice ||
    (processedData.value.data && processedData.value.data.byDevice) ||
    {};

  if (Object.keys(byDeviceData).length === 0) return [];

  return Object.entries(byDeviceData).map(([device, data]) => {
    // data bir sayı, dizi veya nesne olabilir
    let activeUsers = 0;

    if (typeof data === "number") {
      activeUsers = data;
    } else if (Array.isArray(data)) {
      activeUsers = data.length;
    } else if (data && typeof data === "object") {
      activeUsers = data.activeUsers || data.users || 0;
    }

    return {
      device,
      activeUsers,
    };
  });
});

// Kaynak tablo verileri
export const sourceTableData = computed(() => {
  const bySourceData =
    processedData.value.bySource ||
    (processedData.value.data && processedData.value.data.bySource) ||
    {};

  if (Object.keys(bySourceData).length === 0) return [];

  return Object.entries(bySourceData)
    .map(([source, data]) => {
      // data bir sayı, dizi veya nesne olabilir
      let activeUsers = 0;
      let sessions = 0;

      if (typeof data === "number") {
        activeUsers = data;
        sessions = data;
      } else if (Array.isArray(data)) {
        activeUsers = data.length;
        sessions = data.length;
      } else if (data && typeof data === "object") {
        activeUsers = data.activeUsers || data.users || 0;
        sessions = data.sessions || data.activeUsers || data.users || 0;
      }

      return {
        source,
        activeUsers,
        sessions,
      };
    })
    .sort((a, b) => b.activeUsers - a.activeUsers)
    .slice(0, 10);
});

// Realtime Data Computed Properties
// Gerçek zamanlı timeline verileri
export const realtimeTimelineData = computed(() => {
  const timelineData = realtimeData.value.timeline || {};

  if (Object.keys(timelineData).length === 0) return [];

  // Zaman dilimlerini sıralama (dakika bazında)
  const sortedTimes = Object.keys(timelineData).sort();

  return sortedTimes.map((time) => {
    const timeData = timelineData[time];

    return {
      date: time, // Burada "date" anahtarını kullanıyoruz çünkü VisitorChart bu anahtarı bekliyor
      activeUsers: timeData.activeUsers || 0,
      screenPageViews: timeData.screenPageViews || 0, // Ekstra gösterilecek veri
      eventCount: timeData.eventCount || 0, // Ekstra gösterilecek veri
    };
  });
});

// Gerçek zamanlı ülke verileri
export const realtimeCountryData = computed(() => {
  const byCountryData = realtimeData.value.byCountry || {};

  if (Object.keys(byCountryData).length === 0) return [];

  return Object.entries(byCountryData)
    .map(([country, data]) => {
      return {
        country,
        activeUsers: data.activeUsers || 0,
      };
    })
    .sort((a, b) => b.activeUsers - a.activeUsers)
    .slice(0, 10);
});

// Gerçek zamanlı cihaz verileri
export const realtimeDeviceData = computed(() => {
  const byDeviceData = realtimeData.value.byDevice || {};

  if (Object.keys(byDeviceData).length === 0) return [];

  return Object.entries(byDeviceData).map(([device, data]) => {
    return {
      device,
      activeUsers: data.activeUsers || 0,
    };
  });
});
